import type {
  CampaignAnalyticsGranularity,
  CampaignAnalyticsKpi,
  CampaignAnalyticsTrendPoint,
} from "@adpilot/shared";
import { PerformancePanel } from "@/components/shared/performance/PerformancePanel";
import {
  DEFAULT_PERFORMANCE_SLOTS,
  PERFORMANCE_METRICS,
  type PerformanceMetric,
} from "@/components/shared/performance/performance";
import { useAttributionMix } from "@/hooks/use-attribution-note";
import { useShopScope } from "@/hooks/use-shop-scope";

interface ManagedGroupPerformancePanelProps {
  kpis: CampaignAnalyticsKpi[];
  trend: CampaignAnalyticsTrendPoint[];
  dataThrough: string | null;
  metric: PerformanceMetric;
  granularity: CampaignAnalyticsGranularity;
  onMetricChange: (metric: PerformanceMetric) => void;
  onGranularityChange: (granularity: CampaignAnalyticsGranularity) => void;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  onRetry: () => void;
  detail?: boolean;
  /** Shops behind the numbers; defaults to the analytics scope (the list page). */
  shopIds?: readonly string[];
}

/** Managed-group wrapper keeps chart behavior aligned with Campaign/Portfolio. */
export function ManagedGroupPerformancePanel({
  detail = false,
  shopIds,
  ...props
}: ManagedGroupPerformancePanelProps): React.ReactElement {
  const { scopeShopIds } = useShopScope();
  const attributionMix = useAttributionMix(shopIds ?? scopeShopIds);
  return (
    <PerformancePanel
      metrics={PERFORMANCE_METRICS}
      defaultSlots={DEFAULT_PERFORMANCE_SLOTS}
      attributionMix={attributionMix}
      {...props}
      collapseStorageKey={`adpilot:managed-group-performance:${detail ? "detail" : "list"}:v1`}
      slotsStorageKey={`adpilot:managed-group-performance-slots:${detail ? "detail" : "list"}:v1`}
    />
  );
}
